/**
 * ことばの せってい(第一言語 / 学びたい言語)。
 *
 * 海外展開の土台(docs/10_paltown_platform.md §1)。
 *   - 第一言語 = その子が いつも つかう ことば。将来 UI文言と英単語の「いみ」表示に使う
 *   - 学びたい言語 = タイピングで れんしゅう する ことば(いくつでも)
 * えらんだ内容が そのまま「どっちで あそぶ?」のボタンの並びになる。
 *
 * TODO(i18n): この画面の文言自体はまだ日本語固定。
 *   第一言語に合わせた文言の切り替えは別タスク。
 */

import {
  firstLanguageOptions,
  learningLanguageOptions,
  usesJapanese,
  type LanguageCode,
} from '../services/languages';
import type { JapaneseScript } from '../services/settings';
import { useGameStore } from '../store/gameStore';
import './language.css';

/**
 * もじの ひょうじ の選択肢(docs/13)。
 * 「漢字」でも問題文には ふりがな が付くので、迷ったら試して戻せる。
 */
const SCRIPT_OPTIONS: readonly { code: JapaneseScript; label: string; note: string }[] = [
  { code: 'hiragana', label: 'ひらがな', note: 'ぜんぶ かなで よめるよ' },
  { code: 'kanji', label: '漢字(かんじ)', note: 'かんじに ふりがなが つくよ' },
];

export function LanguageSettings() {
  const settings = useGameStore((state) => state.settings);
  const setFirstLanguage = useGameStore((state) => state.setFirstLanguage);
  const toggleLearningLanguage = useGameStore((state) => state.toggleLearningLanguage);
  const setJapaneseScript = useGameStore((state) => state.setJapaneseScript);

  const first: LanguageCode = settings.firstLanguage;
  const learning = settings.learningLanguages;
  // 日本語を まったく使わない子には無関係な項目なので出さない(docs/13)。
  const japanese = usesJapanese(first, learning);

  return (
    <section className="card language-settings">
      <h2 className="card-title">🗣 ことばの せってい</h2>

      <fieldset className="lang-group">
        <legend className="lang-legend">いつも つかう ことば</legend>
        <p className="lang-note">
          きみが ふだん はなす ことばだよ。ひとつ えらんでね。
          {/* 保護者向け: この言語が「第一言語」として保存されます。 */}
        </p>
        <div className="lang-choices">
          {firstLanguageOptions().map((option) => (
            <label key={option.code} className={`lang-choice ${option.code === first ? 'on' : ''}`}>
              <input
                type="radio"
                name="first-language"
                value={option.code}
                checked={option.code === first}
                onChange={() => setFirstLanguage(option.code)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="lang-group">
        <legend className="lang-legend">まなびたい ことば</legend>
        <p className="lang-note">れんしゅう したい ことばを えらぼう。いくつでも えらべるよ。</p>
        <div className="lang-choices">
          {learningLanguageOptions(first).map((option) => {
            const checked = learning.includes(option.code);
            return (
              <label key={option.code} className={`lang-choice ${checked ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  value={option.code}
                  checked={checked}
                  onChange={() => toggleLearningLanguage(option.code)}
                />
                {option.label}
              </label>
            );
          })}
        </div>
        {learning.length === 0 ? (
          <p className="lang-empty">
            いま えらんで いる ことばは ないよ。「どっちで あそぶ?」には いつも つかう ことばだけ
            でるよ。
          </p>
        ) : null}
      </fieldset>

      {/* ── もじの ひょうじ(docs/13。日本語を つかう子だけ)── */}
      {japanese ? (
        <fieldset className="lang-group" data-testid="script-settings">
          <legend className="lang-legend">もじの ひょうじ</legend>
          <p className="lang-note">
            まなびの もんだいを どう だすか えらべるよ。かんじには ふりがなが つくよ。
          </p>
          <div className="lang-choices">
            {SCRIPT_OPTIONS.map((option) => (
              <label
                key={option.code}
                className={`lang-choice ${option.code === settings.japaneseScript ? 'on' : ''}`}
                title={option.note}
              >
                <input
                  type="radio"
                  name="japanese-script"
                  value={option.code}
                  checked={option.code === settings.japaneseScript}
                  onChange={() => setJapaneseScript(option.code)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
    </section>
  );
}
